/**
 * src/components/shared/Button.jsx — the ONE centralized button primitive.
 *
 * TICKET-UI-BUTTON-SYSTEM (CEO 2026-06-16). Replaces hand-rolled inline
 * <button style={{background,color,padding}}> across the app, which kept
 * re-introducing the white-on-white / low-contrast bug class and let palette +
 * padding drift per component. Everything here is token-driven, so changing one
 * token in src/styles.js (e.g. --navy-surface or --btn-pad-md) restyles every
 * button app-wide — the CEO's "adjust in one place" requirement.
 *
 * Encodes src/components/CLAUDE.md BUTTON RULES:
 *   - minHeight 44 (icon-only = 44x44)        - border:none (outline/inset for edges)
 *   - cursor pointer / default when disabled  - disabled = opacity 0.5 (visible, not hidden)
 *   - padding from the central --btn-pad-* tokens (>=8px 12px)
 *   - design tokens only, never hardcoded hex - data-testid passthrough (required)
 *
 * Variants (all readable in BOTH light + dark — verified by the dark-navy
 * sentinel + Button.test.jsx):
 *   primary   filled navy + white      -> var(--navy-surface) bg / var(--on-navy) text  (the inverting-safe pair)
 *   secondary quiet outline            -> transparent bg / var(--navy) text / inset --bdr edge
 *   outline   alias of secondary
 *   ghost     borderless, low emphasis -> transparent bg / var(--t2) text, hover --glass
 *   toggle    segmented control item   -> active = primary, inactive = ghost
 *   danger    destructive              -> var(--bad) bg / var(--on-navy) text
 */
import { forwardRef } from 'react';
import { Loader } from 'lucide-react';

const SPINNER_STYLE = { animation: 'spin 800ms linear infinite', flexShrink: 0 };

/** Background/text pairing per variant. Active state is resolved by the caller. */
function variantStyle(variant, active) {
  switch (variant) {
    case 'danger':
      return { background: 'var(--bad)', color: 'var(--on-navy)', boxShadow: 'none' };
    case 'secondary':
    case 'outline':
      return { background: 'transparent', color: 'var(--navy)', boxShadow: 'inset 0 0 0 1px var(--bdr)' };
    case 'ghost':
      return { background: 'transparent', color: 'var(--t2)', boxShadow: 'none' };
    case 'toggle':
      return active
        ? { background: 'var(--navy-surface)', color: 'var(--on-navy)', boxShadow: 'none' }
        : { background: 'transparent', color: 'var(--t2)', boxShadow: 'none' };
    case 'primary':
    default:
      return { background: 'var(--navy-surface)', color: 'var(--on-navy)', boxShadow: 'none' };
  }
}

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    active = false,
    disabled = false,
    loading = false,
    iconOnly = false,
    fullWidth = false,
    leftIcon = null,
    rightIcon = null,
    type = 'button',
    onClick,
    children,
    style: styleOverride,
    ...rest
  },
  ref
) {
  const isOff = disabled || loading;
  const vstyle = variantStyle(variant, active);

  if (import.meta?.env?.DEV && rest['data-testid'] === undefined && !rest['aria-label']) {
    // Every interactive element needs a stable test/aria hook (CLAUDE.md rule 7).
    console.warn('[Button] missing data-testid (and aria-label) — add one for tests + a11y.');
  }

  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--btn-gap)',
    minHeight: 44,
    borderRadius: 'var(--btn-radius)',
    border: 'none',
    fontSize: size === 'sm' ? 13 : 14,
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: isOff ? 'default' : 'pointer',
    opacity: isOff ? 0.5 : 1,
    transition: 'background 150ms var(--ease), opacity 150ms var(--ease)',
    whiteSpace: 'nowrap',
    ...(iconOnly
      ? { width: 44, height: 44, padding: 0 }
      : { padding: size === 'sm' ? 'var(--btn-pad-sm)' : 'var(--btn-pad-md)' }),
    ...(fullWidth ? { width: '100%' } : {}),
    ...vstyle,
    ...styleOverride,
  };

  const handleClick = (e) => {
    if (isOff) return;
    onClick?.(e);
  };

  return (
    <button
      ref={ref}
      type={type}
      disabled={isOff}
      aria-busy={loading || undefined}
      onClick={handleClick}
      style={style}
      {...rest}
    >
      {loading ? <Loader size={size === 'sm' ? 14 : 16} style={SPINNER_STYLE} /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  );
});

export default Button;
